import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Coins, Search, Users, CircleDollarSign } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";

/**
 * 🆕 Pagina "Report Provvigioni Broker"
 *  Consuma l'endpoint GET /api/commissions/broker-report che filtra le polizze
 *  per DATA DI INCASSO e ritorna la ripartizione completa per ogni polizza:
 *   - provvigione totale (compagnia → broker)
 *   - quota broker / quota collaboratore (retrocessione)
 *   - fee totale / quota broker / quota collaboratore (percent / fixed / none)
 *   - agency_total, collab_total
 */

// Data corrente e primo giorno del mese come default periodo
const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

const FRAZ_LABEL = {
  annuale: "Annuale",
  semestrale: "Semestrale",
  quadrimestrale: "Quadrimestrale",
  trimestrale: "Trimestrale",
  mensile: "Mensile",
};

export default function BrokerCommissionsReport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [brokers, setBrokers] = useState([]);
  const [brokerId, setBrokerId] = useState(searchParams.get("broker_id") || "");
  const [startDate, setStartDate] = useState(searchParams.get("start_date") || firstOfMonth());
  const [endDate, setEndDate] = useState(searchParams.get("end_date") || today());
  const [includeUnpaid, setIncludeUnpaid] = useState(searchParams.get("include_unpaid") === "true");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  // 🆕 Dialog "Registra incasso" per polizze non incassate
  const [payDialog, setPayDialog] = useState(null); // { policy_id, policy_number, premium }
  const [payForm, setPayForm] = useState({ paid_date: today(), paid_amount: "", paid_method: "bonifico", paid_commission: "" });
  const [savingPay, setSavingPay] = useState(false);

  useEffect(() => {
    api.get("/brokers")
      .then((r) => setBrokers(r.data || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const runReport = useCallback(async () => {
    if (!brokerId) { toast.warning("Seleziona un Broker/Agenzia"); return; }
    if (!startDate || !endDate) { toast.warning("Seleziona periodo di incasso"); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/commissions/broker-report", {
        params: { broker_id: brokerId, start_date: startDate, end_date: endDate, include_unpaid: includeUnpaid },
      });
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [brokerId, startDate, endDate, includeUnpaid]);

  // 🆕 Auto-run se il broker è già selezionato al mount (es. ritorno da polizza con return_to)
  useEffect(() => {
    if (brokerId && searchParams.get("broker_id")) {
      runReport();
    }
  }, []);

  // 🆕 Apre il dialog "Registra incasso" per una polizza specifica
  const openPayDialog = (policy) => {
    setPayDialog(policy);
    setPayForm({
      paid_date: today(),
      paid_amount: policy.premium || "",
      paid_method: "bonifico",
      paid_commission: policy.commission_total || "",
    });
  };
  const savePayment = async () => {
    if (!payDialog) return;
    if (!payForm.paid_amount || Number(payForm.paid_amount) <= 0) {
      toast.warning("Inserisci la cifra incassata (> 0)");
      return;
    }
    setSavingPay(true);
    try {
      // Aggiorna la polizza con lo stato pagato — usa PATCH dedicato
      await api.patch(`/policies/${payDialog.policy_id}/payment`, {
        is_paid: true,
        paid_amount: parseFloat(payForm.paid_amount),
        paid_commission: payForm.paid_commission ? parseFloat(payForm.paid_commission) : null,
        paid_date: payForm.paid_date,
        paid_method: payForm.paid_method,
      });
      toast.success(`Incasso registrato per ${payDialog.policy_number || "polizza"}`);
      setPayDialog(null);
      // Ricarica il report per riflettere il nuovo stato
      runReport();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingPay(false);
    }
  };

  const exportCSV = () => {
    if (!report || !report.policies || report.policies.length === 0) {
      toast.warning("Nessun dato da esportare");
      return;
    }
    const cols = [
      "policy_number", "client_name", "company_name", "ramo",
      "paid_at", "net_premium", "commission_percent", "commission_total",
      "fee_total", "broker_fixed_fee", "agency_total",
    ];
    const csv = [
      cols.join(";"),
      ...report.policies.map((p) => cols.map((c) => {
        const v = p[c];
        if (v == null) return "";
        // Escape CSV: quote if contains ; " o \n
        const s = String(v);
        return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(";")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `provvigioni_${report.broker?.name || "broker"}_${startDate}_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV esportato");
  };

  const exportPDF = async () => {
    if (!report || !report.policies || report.policies.length === 0) {
      toast.warning("Nessun dato da esportare");
      return;
    }
    try {
      const { data } = await api.get("/commissions/broker-report/pdf", {
        params: { broker_id: brokerId, start_date: startDate, end_date: endDate, include_unpaid: includeUnpaid },
        responseType: "blob",
      });
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (report.broker?.name || "broker").replace(/[^A-Za-z0-9_-]+/g, "_");
      a.download = `provvigioni_${safe}_${startDate}_${endDate}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF esportato");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totals = report?.totals || {};
  const rows = report?.policies || [];
  const byCollab = report?.by_collaborator || [];

  const feeModeBadge = (mode) => {
    const map = {
      percent: { label: "%", cls: "bg-blue-100 text-blue-700" },
      fixed: { label: "fisso", cls: "bg-purple-100 text-purple-700" },
      none: { label: "no", cls: "bg-slate-100 text-slate-500" },
    };
    const m = map[mode] || map.none;
    return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
  };

  const hasData = useMemo(() => rows.length > 0, [rows]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="broker-commissions-report-page">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-pink-600 mb-2">Provvigioni</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
            <Coins className="h-8 w-8 sm:h-10 sm:w-10 text-pink-600 bg-pink-100 rounded-lg p-1.5" />
            Report Provvigioni Broker
          </h1>
          <p className="text-slate-500 mt-1">
            Analisi provvigioni e fee per Broker/Agenzia in un periodo · filtro per <b>data di incasso</b>
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => window.location.assign("/commissions")}
          data-testid="goto-commissions"
        >
          ← Torna a Provvigioni
        </Button>
      </header>

      {/* Filtri */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-testid="report-filters">
        <div className="md:col-span-4">
          <Label className="text-xs uppercase tracking-wide text-slate-600">Broker / Agenzia</Label>
          <Select value={brokerId} onValueChange={setBrokerId}>
            <SelectTrigger data-testid="report-broker-select" className="bg-white">
              <SelectValue placeholder="Seleziona partner…" />
            </SelectTrigger>
            <SelectContent>
              {brokers.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.name} · {b.type === "agenzia" ? "Agenzia" : "Broker"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs uppercase tracking-wide text-slate-600">Data incasso · Dal</Label>
          <DatePicker value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="report-start-date" />
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs uppercase tracking-wide text-slate-600">Data incasso · Al</Label>
          <DatePicker value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="report-end-date" />
        </div>
        <div className="md:col-span-2 flex gap-2">
          <Button
            className="w-full bg-pink-600 hover:bg-pink-700 text-white"
            disabled={loading || !brokerId}
            onClick={runReport}
            data-testid="report-run-btn"
          >
            <Search className="h-4 w-4 mr-1" /> {loading ? "Calcolo…" : "Genera"}
          </Button>
        </div>
        {/* 🆕 Toggle "Includi polizze non ancora incassate" */}
        <div className="md:col-span-12 flex items-center gap-2 pt-1">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeUnpaid}
              onChange={(e) => setIncludeUnpaid(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-pink-600 focus:ring-pink-400 cursor-pointer"
              data-testid="report-include-unpaid"
            />
            <span className="text-slate-700">
              Includi polizze <b>non ancora incassate</b>
            </span>
            <span className="text-[10.5px] text-slate-500">
              (mostra importi <i>teorici</i> oltre a quelli effettivi)
            </span>
          </label>
        </div>
      </div>

      {/* Nessun dato / no filtro */}
      {!report && (
        <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/50 p-10 text-center text-slate-500" data-testid="report-empty-hint">
          Seleziona un partner e un periodo, poi clicca <b>Genera</b> per calcolare provvigioni e fee.
        </div>
      )}

      {report && !hasData && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-6 text-amber-800 space-y-3" data-testid="report-no-data">
          <p>
            Nessuna polizza {includeUnpaid ? "" : "incassata "}trovata per <b>{report.broker?.name}</b> nel periodo <b>{formatDate(startDate)}</b> — <b>{formatDate(endDate)}</b>.
          </p>
          {/* 🆕 Diagnostiche: dice all'operatore PERCHÉ il report è vuoto */}
          {report.diagnostics && report.diagnostics.total_broker_policies > 0 && (
            <div className="text-sm bg-white/60 border border-amber-200 rounded p-3 space-y-1" data-testid="report-diagnostics">
              <p className="font-semibold text-amber-900">🔍 Diagnostica del broker:</p>
              <ul className="ml-4 list-disc text-amber-800">
                <li>Polizze del broker nel sistema: <b>{report.diagnostics.total_broker_policies}</b></li>
                {report.diagnostics.in_range > 0 && (
                  <li>Con data d&apos;incasso nel periodo: <b>{report.diagnostics.in_range}</b></li>
                )}
                {report.diagnostics.without_paid_at > 0 && (
                  <li>
                    <b>{report.diagnostics.without_paid_at}</b> polizza/e senza <b>data d&apos;incasso</b> impostata
                    {!includeUnpaid && (
                      <span> — attiva <b>&ldquo;Includi polizze non ancora incassate&rdquo;</b> per vederle come importi teorici</span>
                    )}
                  </li>
                )}
                {report.diagnostics.outside_range > 0 && (
                  <li><b>{report.diagnostics.outside_range}</b> polizza/e incassata/e <b>fuori dal periodo</b> selezionato — allarga le date per includerle</li>
                )}
              </ul>
            </div>
          )}
          {report.diagnostics && report.diagnostics.total_broker_policies === 0 && (
            <div className="text-sm bg-white/60 border border-amber-200 rounded p-3" data-testid="report-diagnostics">
              🔍 Il broker <b>{report.broker?.name}</b> non ha polizze assegnate nel sistema (nessuna polizza ha <code>broker_id</code> = questo partner).
            </div>
          )}
        </div>
      )}

      {hasData && (
        <>
          {/* Cards totali */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-totals-cards">
            <TotalCard label="Polizze" value={totals.count} money={false} accent="slate" />
            <TotalCard label="Premi lordi" value={totals.premium_total} money accent="slate" />
            <TotalCard label="Provvigione totale" value={totals.commission_total} money accent="blue" />
            <TotalCard label="Fee totale" value={totals.fee_total} money accent="blue" />
            <TotalCard label="Compenso fisso (agenzia)" value={totals.broker_fixed_fee_total} money accent="indigo" />
            <TotalCard label="Totale agenzia" value={totals.agency_grand_total} money accent="emerald" strong />
          </div>

          {/* Aggregato per collaboratore */}
          {/* Dettaglio per polizza */}
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="report-policies-table">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-800">Dettaglio Polizze ({rows.length})</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={exportPDF} data-testid="report-export-pdf">
                  📄 Esporta PDF
                </Button>
                <Button variant="outline" size="sm" onClick={exportCSV} data-testid="report-export-csv">
                  📥 Esporta CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="report-print">
                  🖨️ Stampa
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50">
                    <th className="py-2 px-2">Polizza</th>
                    <th className="py-2 px-2">Cliente</th>
                    <th className="py-2 px-2">Compagnia</th>
                    <th className="py-2 px-2">Ramo</th>
                    <th className="py-2 px-2">Incasso</th>
                    <th className="py-2 px-2 text-right">Netto</th>
                    <th className="py-2 px-2 text-right">% Prov.</th>
                    <th className="py-2 px-2 text-right" title="Provvigione totale dalla compagnia">Prov. tot.</th>
                    <th className="py-2 px-2 text-right" title="Quota provvigione che resta alla tua agenzia">Prov. → Ag.</th>
                    <th className="py-2 px-2 text-right" title="Fee totale (diritto di intermediazione)">Fee</th>
                    <th className="py-2 px-2 text-right" title="Quota fee che resta alla tua agenzia">Fee → Ag.</th>
                    <th className="py-2 px-2 text-right">Comp. fisso</th>
                    <th className="py-2 px-2 text-right font-bold bg-emerald-100 text-emerald-900" title="Importo netto alla TUA agenzia">💰 Agenzia</th>
                    <th className="py-2 px-2 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr
                      key={p.policy_id}
                      className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${p.is_unpaid ? "bg-amber-50/40" : ""}`}
                      onDoubleClick={() => {
                        const params = new URLSearchParams({
                          broker_id: brokerId,
                          start_date: startDate,
                          end_date: endDate,
                          include_unpaid: String(includeUnpaid),
                        }).toString();
                        const returnTo = `/commissions/broker-report?${params}`;
                        navigate(`/policies?focus=${p.policy_id}&return_to=${encodeURIComponent(returnTo)}`);
                      }}
                      title="Doppio click per aprire la polizza (potrai tornare a questo report)"
                      data-testid={`broker-report-row-${p.policy_id}`}
                    >
                      <td className="py-1.5 px-2 font-medium">
                        {p.policy_number || "—"}
                        {p.is_unpaid && (
                          <span className="ml-1 text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-200 text-amber-800" title="Polizza non ancora incassata: importi teorici">
                            ⏳ non incassata
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 px-2">{p.client_name}</td>
                      <td className="py-1.5 px-2">{p.company_name}</td>
                      <td className="py-1.5 px-2 text-slate-500 text-xs">{p.ramo}</td>
                      <td className="py-1.5 px-2 text-xs">{formatDate(p.paid_at) || "—"}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{formatCurrency(p.net_premium)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-xs">{p.commission_percent}%</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{formatCurrency(p.commission_total)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-emerald-700 font-medium" title="Quota provvigione alla tua agenzia">{formatCurrency(p.agency_from_commission || 0)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{formatCurrency(p.fee_total)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-emerald-700 font-medium" title="Quota fee alla tua agenzia">{formatCurrency(p.agency_from_fee || 0)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-indigo-700" title="Compenso fisso pattuito col broker/agenzia — 100% agenzia">{formatCurrency(p.broker_fixed_fee || 0)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums font-bold text-emerald-800 bg-emerald-50" data-testid={`report-agency-total-${p.policy_id}`}>{formatCurrency(p.agency_total)}</td>
                      <td className="py-1.5 px-2 text-right">
                        {p.is_unpaid && (
                          <button
                            type="button"
                            onClick={() => openPayDialog(p)}
                            className="text-[10.5px] font-semibold px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap"
                            title="Registra l'incasso di questa polizza"
                            data-testid={`register-payment-${p.policy_id}`}
                          >
                            <CircleDollarSign className="inline h-3 w-3 mr-0.5" />
                            Registra incasso
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 font-semibold text-sm">
                  <tr className="border-t-2 border-slate-300">
                    <td colSpan={5} className="py-2 px-2 text-right">TOTALI</td>
                    <td className="py-2 px-2 text-right tabular-nums">{formatCurrency(totals.net_premium_total)}</td>
                    <td />
                    <td className="py-2 px-2 text-right tabular-nums">{formatCurrency(totals.commission_total)}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-emerald-800 font-bold">{formatCurrency(totals.agency_from_commission_total)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{formatCurrency(totals.fee_total)}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-emerald-800 font-bold">{formatCurrency(totals.agency_from_fee_total)}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-indigo-800">{formatCurrency(totals.broker_fixed_fee_total)}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-emerald-900 bg-emerald-200 font-bold text-base" data-testid="report-agency-grand-total">{formatCurrency(totals.agency_grand_total)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}

      {/* 🆕 Dialog Registra incasso */}
      <Dialog open={!!payDialog} onOpenChange={(o) => !o && setPayDialog(null)}>
        <DialogContent className="max-w-md" data-testid="register-payment-dialog">
          <DialogHeader>
            <DialogTitle>💶 Registra incasso polizza</DialogTitle>
          </DialogHeader>
          {payDialog && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Polizza <b>{payDialog.policy_number || "—"}</b> · Cliente <b>{payDialog.client_name}</b>
              </p>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Data incasso</Label>
                <DatePicker
                  value={payForm.paid_date}
                  onChange={(e) => setPayForm((f) => ({ ...f, paid_date: e.target.value }))}
                  data-testid="pay-date"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Cifra incassata (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={payForm.paid_amount}
                  onChange={(e) => setPayForm((f) => ({ ...f, paid_amount: e.target.value }))}
                  data-testid="pay-amount"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Provvigione incassata (€, opz.)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={payForm.paid_commission}
                  onChange={(e) => setPayForm((f) => ({ ...f, paid_commission: e.target.value }))}
                  placeholder="Es. commissione trattenuta"
                  data-testid="pay-commission"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Metodo pagamento</Label>
                <Select value={payForm.paid_method} onValueChange={(v) => setPayForm((f) => ({ ...f, paid_method: v }))}>
                  <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contanti">Contanti</SelectItem>
                    <SelectItem value="bonifico">Bonifico</SelectItem>
                    <SelectItem value="pos">POS</SelectItem>
                    <SelectItem value="assegno">Assegno</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDialog(null)}>Annulla</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={savingPay} onClick={savePayment} data-testid="pay-confirm">
              {savingPay ? "Salvo…" : "✅ Registra incasso"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TotalCard({ label, value, money, accent, strong }) {
  const accents = {
    slate: "bg-slate-50 border-slate-200 text-slate-800",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-900",
  };
  const cls = accents[accent] || accents.slate;
  return (
    <div className={`rounded-lg border p-3 ${cls}`}>
      <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
      <p className={`tabular-nums ${strong ? "text-xl font-bold" : "text-lg font-semibold"} mt-0.5`}>
        {money ? formatCurrency(value || 0) : (value ?? 0)}
      </p>
    </div>
  );
}
